import React from 'react';
import {useTranslation} from 'react-i18next';

import {Spinner} from '@ELib/assets/images';
import {useKeyboard} from '@ELib/utilities/hooks/keyboardHandler';

import {BaseButton, BaseButtonProps, ButtonProps} from './BaseButton';
import {StyledButton} from './ButtonElements';

const BaseEButton = (props: BaseButtonProps) => (
  <BaseButton {...props} ButtonElement={StyledButton} />
);

//#region Default Buttons
export const EButton = (props: BaseButtonProps): React.ReactElement => (
  <BaseEButton type='submit' {...props} />
);

export const NEButton = (props: BaseButtonProps): React.ReactElement => (
  <BaseEButton type='submit' {...props} newStyle />
);
//#endregion

//#region Submit Buttons
type SubmitButtonProps = Partial<ButtonProps> & {
  isSubmitting: boolean;
};

export const ESubmitButton = (props: SubmitButtonProps): React.ReactElement => (
  <BaseESubmitButton {...props} />
);

export const ESubmitDangerButton = (
  props: SubmitButtonProps
): React.ReactElement => <BaseESubmitButton {...props} variant='danger' />;

const BaseESubmitButton = ({
  isSubmitting,
  children,
  ...props
}: Partial<BaseButtonProps> & {
  isSubmitting: boolean;
}): React.ReactElement => {
  const {t} = useTranslation('lib');
  return (
    <EButton Icon={isSubmitting ? Spinner : undefined} {...props}>
      {children ? children : t('actions.save')}
    </EButton>
  );
};
//#endregion

//#region Flat Buttons
export const EFlatButton = (props: ButtonProps): React.ReactElement => (
  <BaseEButton type='button' {...props} variant='flat' />
);

export const ECancelButton = (
  props: Omit<ButtonProps, 'children'>
): React.ReactElement => {
  const {t} = useTranslation('lib');
  const clickCancelOnEscKeyDown = (e: KeyboardEvent) => {
    if (props.onClick && e.key === 'Escape') {
      props.onClick(
        e as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>
      );
    }
  };
  useKeyboard(clickCancelOnEscKeyDown);
  return <EFlatButton {...props}>{t('actions.cancel')}</EFlatButton>;
};
//#endregion

export const EOutlineButton = (props: ButtonProps): React.ReactElement => (
  <BaseEButton type='button' {...props} variant='outline' />
);

export const NEOutlineButton = (props: ButtonProps): React.ReactElement => (
  <BaseEButton type='button' {...props} variant='outline' newStyle />
);

//#region Danger Buttons
export const EDangerButton = (props: ButtonProps): React.ReactElement => (
  <BaseEButton type='button' {...props} variant='danger' />
);

export const NEDangerButton = (props: ButtonProps): React.ReactElement => (
  <BaseEButton type='button' {...props} variant='danger' newStyle />
);

export const EDeleteButton = (
  props: Omit<ButtonProps, 'children'>
): React.ReactElement => {
  const {t} = useTranslation('lib');
  return <EDangerButton {...props}>{t('actions.delete')}</EDangerButton>;
};
//#endregion
